// The Datadog react-router integration tracks route changes as named RUM views.
// It officially supports react-router 6/7 while the suite is on 8; the wrapped
// factories call only exports that v8 still provides, so this is a deliberate
// off-range use. See docs/adr/0004-datadog-rum-observability.md (RND-11809).
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7'
import { useEffect } from 'react'
import { Provider } from 'react-redux'
import { createMemoryRouter, RouterProvider } from 'react-router'

import { FirstUserInteractionTracker } from './analytics/FirstUserInteractionTracker'
import { sec } from './api/rtk/security'
import { setRouter } from './app/router'
import { routes } from './app/routes'
import { useRegardAuth } from './auth/useRegardAuth'
import { shouldBypassAuthForE2E } from './config'
import { AmplitudeIdentityBridge } from './integrations/amplitude/AmplitudeIdentityBridge'
import {
  AppErrorBoundary,
  ErrorFallback,
} from './integrations/datadog/AppErrorBoundary'
import { DatadogIdentityBridge } from './integrations/datadog/DatadogIdentityBridge'
import { store } from './redux/store'

// Registered so the callbacks that navigate the suite from outside the React tree can reach it (see
// `shell/app/router.ts`), which is also what makes `Auth0ProviderWithConfig` independent of this module.
const router = createBrowserRouter(routes)
setRouter(router)

/**
 * Bridges the session to the RTK Query security helper.
 *
 * The base query runs outside the React tree, so it cannot read the session itself; this registers the two
 * operations it needs, early enough that `sec.getAccessTokenSilently` resolves real tokens before any
 * authenticated query is issued. Both come from `useRegardAuth`, so the base query sends an **EMR-embedded
 * mode** session's token as readily as a standalone one and recovers a dead session the way that mode
 * recovers one, without this bridge knowing which mode it is in (Atlas ADR-0027).
 *
 * `reauth` is what the base query and the 401 middleware call through `sec.requestReauth`. Renders nothing.
 */
const AuthTokenBridge = () => {
  const { getSessionAccessToken, reauth } = useRegardAuth()

  useEffect(() => {
    // Under the env-gated E2E bypass there is no real Auth0 session to bridge,
    // so leave the security handlers unset; the base query skips the token fetch.
    if (shouldBypassAuthForE2E) {
      return
    }

    sec.setAccessTokenSilentlyHandler(getSessionAccessToken)
    sec.setReauthHandler(reauth)
  }, [getSessionAccessToken, reauth])

  return null
}

export const App = () => (
  <Provider store={store}>
    <AuthTokenBridge />
    <AmplitudeIdentityBridge />
    <DatadogIdentityBridge />
    <FirstUserInteractionTracker />
    <div id="app">
      <AppErrorBoundary fallback={ErrorFallback}>
        <RouterProvider router={router} />
      </AppErrorBoundary>
    </div>
  </Provider>
)

/**
 * Test-friendly version of the App backed by a memory router.
 *
 * Use this in tests instead of `App` so routing can be exercised without a real
 * browser. Mirrors the atlas service's `TestApp`: it wraps the same `routes` in a
 * `createMemoryRouter` seeded with `initialEntries` and the redux `Provider`.
 */
export const TestApp = ({
  initialEntries = ['/'],
}: {
  initialEntries?: string[]
}) => {
  const testRouter = createMemoryRouter(routes, { initialEntries })
  return (
    <Provider store={store}>
      <div id="app">
        <RouterProvider router={testRouter} />
      </div>
    </Provider>
  )
}
